import { useApiClient } from "~/composables/useApiClient";
import { CatalogType } from "~/constants/catalog";
import type {
  ExperienceEntityType,
  ExperienceType
} from "~/constants/experience";
import type { UserSkillEntityType } from "~/constants/user";
export interface BodyMatchModelEvaluate {
  questionary_id: number;
}

export type UserSkillToCreate = {
  skill_id: number;
  entity_type: UserSkillEntityType;
  entity_id: number;
  metadata: UserSkillMetadata;
};

interface UserSkillApi {
  id: number;
  user_id: number;
  skill_id: number;
  entity_type: string;
  entity_id: number;
  metadata?: UserSkillMetadata;
  skill?: Skill;
  user?: User;
}

class UserSkillsMapperToDomain implements DataMapper<
  Partial<UserSkillApi>[],
  UserSkill[]
> {
  map(apiModels: Partial<UserSkillApi>[]): UserSkill[] {
    return apiModels.map((apiModel) => ({
      id: apiModel.id as number,
      userId: apiModel.user_id as number,
      skillId: apiModel.skill_id as number,
      entityType: apiModel.entity_type as UserSkillEntityType,
      entityId: apiModel.entity_id as number,
      metadata: apiModel.metadata,
      skill: apiModel.skill,
      user: apiModel.user
    }));
  }
}

interface ExperienceApi {
  id: number;
  user: number;
  experience_type: string;
  entity_id: number;
  entity_type: string;
  start_date: string | null;
  end_date: string | null;
  progress: number | null;
  skills: number[];
  metadata?: {
    company?: string;
    business_area?: string;
    title?: string;
    educational_type?: string;
    educational_place?: string;
    educational_area?: string;
    educational_status?: string;
    educational_specialty?: string;
    is_current_role?: boolean;
  };
  created_at: string;
}

class ExperienceMapperToDomain implements DataMapper<
  Partial<ExperienceApi>,
  Experience
> {
  map(apiModel: Partial<ExperienceApi>): Experience {
    return {
      id: apiModel.id as number,
      userId: apiModel.user as number,
      experienceType: apiModel.experience_type as ExperienceType,
      entityId: apiModel.entity_id as number,
      entityType: apiModel.entity_type as ExperienceEntityType,
      startDate: apiModel.start_date ? new Date(apiModel.start_date) : null,
      endDate: apiModel.end_date ? new Date(apiModel.end_date) : null,
      progress: apiModel.progress as number | null,
      skills: apiModel.skills as number[],
      metadata: {
        totalSkills: `${(apiModel.skills || []).length} habilidades adquiridas`,
        company: apiModel.metadata?.company,
        businessArea: apiModel.metadata?.business_area,
        title: apiModel.metadata?.title,
        educationalType: apiModel.metadata?.educational_type,
        educationalPlace: apiModel.metadata?.educational_place,
        educationalArea: apiModel.metadata?.educational_area,
        educationalStatus: apiModel.metadata?.educational_status,
        educationalSpecialty: apiModel.metadata?.educational_specialty
      }
    };
  }
}

class ExperienceMapperToApi implements DataMapper<
  Experience,
  Partial<ExperienceApi>
> {
  map(domainModel: Experience): Partial<ExperienceApi> {
    return {
      id: domainModel.id,
      user: domainModel.userId,
      experience_type: domainModel.experienceType,
      entity_id: domainModel.entityId,
      entity_type: domainModel.entityType,
      start_date: domainModel.startDate
        ? domainModel.startDate.toISOString()
        : null,
      end_date: domainModel.endDate ? domainModel.endDate.toISOString() : null,
      progress: domainModel.progress,
      skills: domainModel.skills,
      metadata: {
        company: domainModel.metadata?.company,
        business_area: domainModel.metadata?.businessArea,
        title: domainModel.metadata?.title,
        educational_type: domainModel.metadata?.educationalType,
        educational_place: domainModel.metadata?.educationalPlace,
        educational_area: domainModel.metadata?.educationalArea,
        educational_status: domainModel.metadata?.educationalStatus,
        educational_specialty: domainModel.metadata?.educationalSpecialty
      }
    };
  }
}
export interface ExperienceEntityFilter {
  entity_id: number;
  entity_type: ExperienceEntityType;
}
class ExperiencesMapperToDomain implements DataMapper<
  Partial<ExperienceApi>[],
  Experience[]
> {
  map(apiModels: Partial<ExperienceApi>[]): Experience[] {
    return apiModels.map((apiModel) =>
      new ExperienceMapperToDomain().map(apiModel)
    );
  }
}

interface CatalogApi {
  id: number;
  name: string;
  type: string;
  description?: string;
}
class CatalogMapperToDomain implements DataMapper<
  Partial<CatalogApi>,
  Catalog
> {
  map(apiModel: Partial<CatalogApi>): Catalog {
    return {
      id: apiModel.id as number,
      name: apiModel.name as string,
      type: apiModel.type as CatalogType,
      description: apiModel.description
    };
  }
}

class CatalogsMapperToDomain implements DataMapper<
  Partial<CatalogApi>[],
  Catalog[]
> {
  map(apiModels: Partial<CatalogApi>[]): Catalog[] {
    return apiModels.map((apiModel) =>
      new CatalogMapperToDomain().map(apiModel)
    );
  }
}

interface UserNotificationApi {
  id: number;
  message: string;
  is_active: boolean;
}

class UserNotificationsMapperToDomain implements DataMapper<
  Partial<UserNotificationApi>[],
  UserNotification[]
> {
  map(apiModels: Partial<UserNotificationApi>[]): UserNotification[] {
    return apiModels.map((m) => ({
      id: m.id as number,
      message: m.message as string,
      isActive: m.is_active as boolean
    }));
  }
}

interface LearningPathApi {
  user_id: number;
  courses: {
    id: number;
    name: string;
    level: string;
    duration_in_hours: number;
    time_shift: string;
  }[];
}

class LearningPathMapperToDomain implements DataMapper<
  Partial<LearningPathApi> | null,
  LearningPath | null
> {
  map(apiModel: Partial<LearningPathApi> | null): LearningPath | null {
    if (!apiModel) return null;
    return {
      userId: apiModel.user_id as number,
      courses:
        apiModel.courses?.map((course) => ({
          id: course.id,
          name: course.name,
          level: course.level,
          durationInHours: course.duration_in_hours,
          timeShift: course.time_shift
        })) || []
    };
  }
}

export interface UserRepository {
  findMyExperiences: () => Promise<ResponseApi<Experience[]>>;
  findExperience: (id: number) => Promise<ResponseApi<Experience>>;
  createExperience: (data: Experience) => Promise<ResponseApi<Experience>>;
  deleteExperience: (id: number) => Promise<ResponseApi<void>>;
  updateExperience: (
    id: number,
    data: Experience
  ) => Promise<ResponseApi<Experience>>;
  findExperienceEntitySkills: (
    entityFilter: ExperienceEntityFilter
  ) => Promise<ResponseApi<Skill[]>>;
  evaluateMatchModels: (
    data: BodyMatchModelEvaluate
  ) => Promise<ResponseApi<void>>;
  getMySkills: (populate?: string[]) => Promise<ResponseApi<UserSkill[]>>;
  saveUserSkill: (data: UserSkillToCreate) => Promise<ResponseApi<number>>;
  saveUserSkills: (data: UserSkillToCreate[]) => Promise<ResponseApi<void>>;
  deleteUserSkill: (id: number) => Promise<ResponseApi<void>>;
  findCatalogs: (type: string) => Promise<ResponseApi<Catalog[]>>;
  getMyLearningPath: () => Promise<ResponseApi<LearningPath | null>>;
  getMyNotifications: () => Promise<ResponseApi<UserNotification[]>>;
  markNotificationsAsRead: (ids: number[]) => Promise<ResponseApi<void>>;
}
export const useUserRepository: () => UserRepository = () => {
  const findMyExperiences = (): Promise<ResponseApi<Experience[]>> => {
    return useApiClient("/users/me/experiences", {
      method: "GET",
      mapper: new ExperiencesMapperToDomain()
    });
  };

  const findExperience = (id: number): Promise<ResponseApi<Experience>> => {
    return useApiClient(`/users/experiences/${id}`, {
      method: "GET",
      mapper: new ExperienceMapperToDomain()
    });
  };
  const createExperience = (
    data: Experience
  ): Promise<ResponseApi<Experience>> => {
    return useApiClient("/users/experiences", {
      method: "POST",
      body: new ExperienceMapperToApi().map(data),
      mapper: new ExperienceMapperToDomain()
    });
  };
  const deleteExperience = (id: number): Promise<ResponseApi<void>> => {
    return useApiClient(`/users/experiences/${id}`, {
      method: "DELETE"
    });
  };
  const updateExperience = (
    id: number,
    data: Experience
  ): Promise<ResponseApi<Experience>> => {
    return useApiClient(`/users/experiences/${id}`, {
      method: "PUT",
      body: new ExperienceMapperToApi().map(data),
      mapper: new ExperienceMapperToDomain()
    });
  };
  const findExperienceEntitySkills = (
    entityFilter: ExperienceEntityFilter
  ): Promise<ResponseApi<Skill[]>> => {
    return useApiClient("/users/experiences/entity-skills", {
      method: "GET",
      query: { ...entityFilter }
    });
  };

  const evaluateMatchModels = async (
    body: BodyMatchModelEvaluate
  ): Promise<ResponseApi<void>> => {
    return await useApiClient("/users/matchs/evaluate-models", {
      method: "POST",
      body
    });
  };

  const getMySkills = (
    populate?: string[]
  ): Promise<ResponseApi<UserSkill[]>> => {
    const query = {};
    if (populate) {
      Object.assign(query, { populate: populate.join(",") });
    }
    return useApiClient("/users/me/skills", {
      method: "GET",
      query,
      mapper: new UserSkillsMapperToDomain(),
      // Agregamos una key única para evitar cachear la respuesta
      key: `users/me/skills-${Date.now()}}`
    });
  };

  const saveUserSkill = (
    data: UserSkillToCreate
  ): Promise<ResponseApi<number>> => {
    return useApiClient("/users/me/skills", {
      method: "POST",
      body: data,
      key: `users/me/skills-save-${Date.now()}`
    });
  };

  const saveUserSkills = (
    data: UserSkillToCreate[]
  ): Promise<ResponseApi<void>> => {
    return useApiClient("/users/me/skills/bulk", {
      method: "POST",
      body: data
    });
  };

  const deleteUserSkill = (userSkillId: number): Promise<ResponseApi<void>> => {
    return useApiClient(`/users/me/skills/${userSkillId}`, {
      method: "DELETE",
      key: `users/me/skills-delete-${userSkillId}-${Date.now()}`
    });
  };

  const findCatalogs = (type: string): Promise<ResponseApi<Catalog[]>> => {
    return useApiClient("/catalogs", {
      method: "GET",
      query: { type },
      mapper: new CatalogsMapperToDomain()
    });
  };

  const getMyLearningPath = (): Promise<ResponseApi<LearningPath | null>> => {
    const token = useCookie("auth_jwt");
    const RESULTS_MOCK: Record<string, LearningPathApi | null> = {
      "questionary.finished-1-lp@dtp.cl": {
        user_id: 1,
        courses: [
          {
            id: 1,
            name: "Curso de JavaScript",
            level: "Básico",
            duration_in_hours: 10,
            time_shift: "Mañana"
          },
          {
            id: 2,
            name: "Curso de TypeScript",
            level: "Intermedio",
            duration_in_hours: 15,
            time_shift: "Tarde"
          },
          {
            id: 3,
            name: "Curso de Vue.js",
            level: "Intermedio",
            duration_in_hours: 20,
            time_shift: "Noche"
          }
        ]
      },
      "questionary.finished-2@dtp.cl": {
        user_id: 1,
        courses: [
          {
            id: 1,
            name: "Curso de JavaScript",
            level: "Básico",
            duration_in_hours: 10,
            time_shift: "Mañana"
          },
          {
            id: 2,
            name: "Curso de TypeScript",
            level: "Intermedio",
            duration_in_hours: 15,
            time_shift: "Tarde"
          },
          {
            id: 3,
            name: "Curso de Vue.js",
            level: "Intermedio",
            duration_in_hours: 20,
            time_shift: "Noche"
          },
          {
            id: 4,
            name: "Curso de Node.js",
            level: "Avanzado",
            duration_in_hours: 25,
            time_shift: "Mañana"
          }
        ]
      }
    };
    const result = RESULTS_MOCK[token.value || ""] || null;
    return useApiClient(
      "/users/me/learning-path",
      {
        method: "GET",
        mapper: new LearningPathMapperToDomain()
      },
      {
        data: result as LearningPathApi,
        delay: 1000
      }
    );
  };

  const getMyNotifications = (): Promise<ResponseApi<UserNotification[]>> => {
    return useApiClient("/users/me/notifications", {
      method: "GET",
      mapper: new UserNotificationsMapperToDomain()
    });
  };

  const markNotificationsAsRead = (
    ids: number[]
  ): Promise<ResponseApi<void>> => {
    return useApiClient("/users/me/notifications/read", {
      method: "PUT",
      body: { ids }
    });
  };

  return {
    findMyExperiences,
    findExperience,
    createExperience,
    updateExperience,
    evaluateMatchModels,
    getMySkills,
    saveUserSkill,
    saveUserSkills,
    deleteUserSkill,
    findExperienceEntitySkills,
    deleteExperience,
    findCatalogs,
    getMyLearningPath,
    getMyNotifications,
    markNotificationsAsRead
  };
};
